import React, { type FunctionComponent, useEffect } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { css } from '@emotion/react'
import { FormattedMessage } from 'react-intl'
import isObjectLike from 'lodash/isObjectLike'
import { Text, colors } from '@retailer-platform/shared-components'
import { MerchandisingFormField } from 'domains/merchandising/components/form-field/MerchandisingFormField'
import { MerchandisingFormRow } from 'domains/merchandising/components/form-row/MerchandisingFormRow'
import { MerchandisingFormSelect } from 'domains/merchandising/components/form-input/MerchandisingFormSelect'
import { useIsI18nEnabled } from 'domains/merchandising/utils/useIsI18nEnabled.hooks'
import { useLocales } from 'domains/merchandising/utils/useLocales.hooks'
import flags from 'domains/merchandising/constants/flags'
import languages from 'domains/merchandising/constants/languages'

export const findLocaleKeys = (object, locales) => {
  if (!isObjectLike(object)) {
    return []
  }

  return Object.entries(object).reduce((result, [key, value]) => {
    if (key.startsWith('_')) {
      return result
    }

    return [...result, ...(locales.includes(key) ? [key] : []), ...findLocaleKeys(value, locales)]
  }, [])
}

const localeDescriptionText = css({
  color: colors.GRAYSCALE.X70,
  marginTop: -10,
})

interface Props {
  form: FormContextValues<any>
}

export const localeIdentifier = 'locale'

export const MerchandisingFormLocale: FunctionComponent<Props> = ({ form }) => {
  const locales = useLocales()

  const { getValues, errors, register, setValue } = form

  useEffect(() => {
    const locale = getValues(localeIdentifier)
    const errorLocales = findLocaleKeys(errors, locales)
    if (!errorLocales.some(errorLocale => errorLocale === locale)) {
      const otherLocale = errorLocales.find(errorLocale => errorLocale !== locale)
      if (otherLocale) {
        setValue('locale', otherLocale)
      }
    }
  }, [getValues, errors, locales, setValue])

  const isI18nEnabled = useIsI18nEnabled()
  if (!isI18nEnabled) {
    return null
  }

  if (locales.length === 1) {
    return <input name={localeIdentifier} ref={register} type="hidden" />
  }

  return (
    <>
      <MerchandisingFormRow>
        <MerchandisingFormField>
          <MerchandisingFormSelect
            labelId="merchandising.hero-banners.section.content.locale.label"
            localeIdentifier={localeIdentifier}
            form={form}
            options={locales.map(locale => {
              const [languageCode, country] = locale.split('_')
              return {
                label: `${flags[country]} ${languages[languageCode]}`,
                value: locale,
              }
            })}
          />
        </MerchandisingFormField>
        <MerchandisingFormField />
      </MerchandisingFormRow>
      <MerchandisingFormRow>
        <Text data-testid="description" css={localeDescriptionText} size="small">
          <FormattedMessage id="merchandising.hero-banners.section.content.locale.description" />
        </Text>
      </MerchandisingFormRow>
    </>
  )
}
